import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import MenuIcon from '@material-ui/icons/Menu'
import clsx from 'clsx'
import React from 'react'
import {
    harmonyHoverColors,
    harmonyIconButtonBase,
    useHarmonyIconButtonStyles,
} from 'shared/hooks/useHarmonyIconButtonStyles'
import useIsHarmonyHeader from 'shared/hooks/useIsHarmonyHeader'

const appHeaderHeight = 60
const useStyles = makeStyles((theme: Theme) => ({
    fullHeight: {
        height: appHeaderHeight,
    },
    whiteFont: {
        color: theme.palette.common.white,
    },
    menuButton: {
        fontSize: pxToRem(16),
        fontWeight: 'normal',
        borderRadius: 0,
        padding: theme.spacing(0, 2),
    },
    iconButtonActive: {
        backgroundColor: `rgba(0,0,0,0.25)`,
        opacity: 1,

        '&:hover': {
            backgroundColor: `rgba(0,0,0,0.25)`,
        },
    },
    // Light variant: the dark AppBar's hardcoded white text/active-bg would be
    // invisible on a white header.
    whiteFontLight: {
        color: theme.harmony.colors.textHeader,
    },
    iconButtonActiveLight: {
        backgroundColor: theme.harmony.colors.hoverBg,
        color: theme.harmony.colors.brandMain,
        '&:hover': {
            backgroundColor: theme.harmony.colors.hoverBg,
        },
    },
    // IconButton avoids MUI Button's min-width overriding harmonyIconButtonBase.
    iconOnlyBtn: {
        ...harmonyIconButtonBase(theme),
        '&:hover': {
            ...harmonyHoverColors(theme),
            '& $menuIcon': {
                color: theme.harmony.colors.brandMain,
            },
        },
    },
    menuIcon: {
        width: pxToRem(24),
        height: pxToRem(24),
        color: theme.harmony.colors.textHeader,
    },
}))

export interface NavBarMenuProps {
    onClick?: VoidFunction
    isActive?: boolean
    hideLabel?: boolean
    ariaControls?: string
}

const NavBarMenu: React.FC<NavBarMenuProps> = ({
    onClick,
    isActive = false,
    hideLabel = false,
    ariaControls,
}) => {
    const classes = useStyles()
    const harmonyClasses = useHarmonyIconButtonStyles()
    const isHarmonyHeader = useIsHarmonyHeader()

    if (hideLabel && isHarmonyHeader) {
        return (
            <IconButton
                data-testid="navBarMenuButton"
                aria-label="Toggle navigation menu"
                aria-expanded={isActive}
                aria-controls={ariaControls}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                onClick={onClick}
                className={clsx(
                    classes.iconOnlyBtn,
                    harmonyClasses.focusRing,
                    isActive && classes.iconButtonActiveLight
                )}
            >
                <MenuIcon className={classes.menuIcon} />
            </IconButton>
        )
    }

    return (
        <Button
            data-testid="navBarMenuButton"
            aria-label={hideLabel ? 'Toggle navigation menu' : undefined}
            aria-expanded={isActive}
            aria-controls={ariaControls}
            className={clsx(
                classes.fullHeight,
                classes.whiteFont,
                isHarmonyHeader && classes.whiteFontLight,
                classes.menuButton,
                isActive && classes.iconButtonActive,
                isActive && isHarmonyHeader && classes.iconButtonActiveLight
            )}
            onClick={onClick}
            startIcon={<MenuIcon fontSize="large" />}
        >
            {!hideLabel && 'Menu'}
        </Button>
    )
}

export default NavBarMenu
